import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead, { OrganizationSchema } from '@/components/base/SEOHead';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于九妖漫画网',
    description: aboutInfo.introduction,
    url: 'https://www.jiuyaomanhua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '九妖漫画网',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '九妖漫画网是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，九妖漫画网所有漫画资源完全免费，无需付费订阅，您可以在线免费阅读所有漫画全集。',
        },
      },
      {
        '@type': 'Question',
        name: '九妖漫画网有哪些漫画类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九妖漫画网涵盖热血、恋爱、玄幻、悬疑、搞笑、科幻、都市、古风、冒险、奇幻等多种类型，满足不同读者的阅读需求。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '九妖漫画网每日更新，与出版社同步，第一时间为您带来最新章节。每日更新300+部漫画。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在九妖漫画网上搜索漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在网站顶部的搜索框中输入漫画名称或作者名进行搜索，也可以通过分类页面按类型浏览漫画作品。',
        },
      },
    ],
  };

  function InjectFaqSchema({ schema }: { schema: Record<string, unknown> }) {
    useEffect(() => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return () => {
        document.head.removeChild(script);
      };
    }, [schema]);
    return null;
  }

  return (
    <>
      <SEOHead
        title="关于我们 - 九妖漫画网"
        description="九妖漫画网是国内领先的免费漫画在线阅读平台，致力于为广大漫画爱好者提供高清无删减的漫画资源。了解九妖漫画网的品牌故事、团队理念和联系方式。"
        keywords="九妖漫画网,关于我们,免费漫画,在线漫画,漫画平台,品牌介绍"
        canonicalUrl="https://www.jiuyaomanhua.com/about"
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: '关于我们', href: '/about' },
        ]}
        schema={aboutSchema}
      />
      <OrganizationSchema />
      <InjectFaqSchema schema={faqSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </div>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
          </div>

          {/* Hero */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于九妖漫画网
              </h1>
              <p className="text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                {aboutInfo.tagline}
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">品牌故事</h2>
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                  <p className="text-foreground-600 leading-relaxed mb-4">{aboutInfo.introduction}</p>
                  <p className="text-foreground-600 leading-relaxed">{aboutInfo.story}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="text-center bg-background-50 rounded-xl p-6 border border-background-200/70">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-2">{stat.value}</div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-10 text-center">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {aboutInfo.features.map((feature) => (
                  <div key={feature.title} className="bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mb-4">
                      <i className={`${feature.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2 text-center">用户口碑</h2>
              <p className="text-sm text-foreground-500 text-center mb-10">数百万读者的真实评价</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {aboutInfo.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{testimonial.content}&rdquo;</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                        {testimonial.name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-foreground-700">{testimonial.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2 text-center">联系我们</h2>
              <p className="text-sm text-foreground-500 text-center mb-10">欢迎随时与我们联系</p>

              <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-mail-line', label: '邮箱', value: aboutInfo.contact.email },
                  { icon: 'ri-phone-line', label: '电话', value: aboutInfo.contact.phone },
                  { icon: 'ri-map-pin-line', label: '地址', value: aboutInfo.contact.address },
                  { icon: 'ri-wechat-line', label: '微信', value: aboutInfo.contact.wechat },
                ].map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-xl p-5 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className={`${item.icon} text-lg text-primary-500`}></i>
                    </div>
                    <h3 className="text-xs text-foreground-500 mb-1">{item.label}</h3>
                    <p className="text-sm font-medium text-foreground-700 break-all">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2 text-center">常见问题</h2>
              <p className="text-sm text-foreground-500 text-center mb-10">快速解答您的疑问</p>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  { q: '九妖漫画网是免费的吗？', a: '是的，九妖漫画网所有漫画资源完全免费，无需付费订阅，您可以在线免费阅读所有漫画全集。' },
                  { q: '九妖漫画网有哪些漫画类型？', a: '九妖漫画网涵盖热血、恋爱、玄幻、悬疑、搞笑、科幻、都市、古风、冒险、奇幻等多种类型。' },
                  { q: '漫画多久更新一次？', a: '九妖漫画网每日更新，与出版社同步，第一时间为您带来最新章节，每日更新300+部漫画。' },
                  { q: '如何在网站上搜索漫画？', a: '您可以在网站顶部的搜索框中输入漫画名称或作者名进行搜索，也可以通过分类页面按类型浏览。' },
                  { q: '需要注册账号才能看漫画吗？', a: '不需要，九妖漫画网所有漫画都无需注册即可免费在线阅读，打开网站即可畅享漫画。' },
                ].map((faq, index) => (
                  <details key={index} className="bg-background-50 rounded-xl border border-background-200/70 group">
                    <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">
                      {faq.q}
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-2">
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                      </div>
                    </summary>
                    <div className="px-6 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}